import { Body } from "@sprout/typography";
import { Link } from "@inertiajs/react";
import EditIcon from "@sprout/icons/edit";
import "./entry_point.scss";
import useTranslation from "@hooks/use_translation";
import CompletionTag from "@components/completion_tag.tsx";
import { CompletionStatus, CompletionStatusType } from "@utils/completion_status.ts";
import IdentityVerificationBadge from "@/components/ui/identity_verification_badge";
import useDevice from "@hooks/use_device.ts";
import { Tag } from "@sprout/tag";
import theme from "@/stylesheets/theme.module.scss";
import { SecondaryButton, SecondaryButtonLink } from "@sprout/buttons";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";

type Props = {
  href: string;
  sectionHeader: string;
  fullName?: string;
  email?: string | null;
  completionStatus: CompletionStatusType;
  showIdentityVerificationActionRequired?: boolean;
  isIdentityVerified?: boolean;
  identityVerificationPartner?: string | null;
  pendingIdentityVerificationTaskPartner?: string | null;
};

const EntryPoint = (props: Props) => {
  const {
    href,
    sectionHeader,
    fullName,
    email,
    completionStatus,
    showIdentityVerificationActionRequired = false,
    isIdentityVerified = false,
    identityVerificationPartner,
    pendingIdentityVerificationTaskPartner,
  } = props;
  const { t } = useTranslation("user");
  const { t: tIdentityVerification } = useTranslation("identity_verification");
  const device = useDevice();

  const hasFullName = Boolean(fullName && fullName.trim());
  const actionRequiredProviderName = showIdentityVerificationActionRequired
    ? identityVerificationPartnerConfig(pendingIdentityVerificationTaskPartner).name
    : null;
  const actionRequiredLabel = tIdentityVerification("badge.action_required");

  return (
    <div className="entry-point">
      <Link href={href} className="entry-point__header">
        <Body medium>{sectionHeader}</Body>
        <CompletionTag status={completionStatus} />
        {completionStatus === CompletionStatus.NotStarted ? (
          <>{device.isWeb && <SecondaryButton size="medium">{t("getStarted")}</SecondaryButton>}</>
        ) : (
          <div className="entry-point__header__icon">
            <EditIcon />
          </div>
        )}
      </Link>
      {hasFullName && (
        <div className="flex items-center gap-2">
          <Body medium>{fullName}</Body>
          {isIdentityVerified && (
            <IdentityVerificationBadge partner={identityVerificationPartner} />
          )}
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2 min-w-0">
          <Body light className="truncate" title={email}>
            {email}
          </Body>
          {!hasFullName && isIdentityVerified && (
            <IdentityVerificationBadge partner={identityVerificationPartner} />
          )}
        </div>
      )}
      {showIdentityVerificationActionRequired && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <IdentityVerificationBadge
              partner={pendingIdentityVerificationTaskPartner}
              tooltip={actionRequiredLabel}
            />
            <span className="text-xs font-medium uppercase tracking-wide">
              {actionRequiredProviderName}
            </span>
          </div>
          <Tag label={actionRequiredLabel} color={theme.irisE50} />
        </div>
      )}
      <div className={`entry-point__description ${device.isMobile && "items-center"}`}>
        {device.isMobile && completionStatus === CompletionStatus.NotStarted && (
          <SecondaryButtonLink href={href} size="medium">
            {t("getStarted")}
          </SecondaryButtonLink>
        )}
      </div>
    </div>
  );
};

export default EntryPoint;
